import { generate3DModelFilename } from '@/utils/file-utils';
import { findArtifactFile, Message } from '@shared/types';
import type { ArtifactFile, ParametricArtifact } from '@shared/types';
import { downloadConversationAssetBlob } from '@/lib/conversationAssets';
import i18n from '@/i18n/i18n';

interface DownloadOptions {
  content: Blob | string;
  filename: string;
  mimeType?: string;
}

interface GenerateDownloadFilenameOptions {
  currentMessage?: Message | null;
  fallback?: string;
  extension: string;
}

/**
 * Downloads a file by creating a temporary download link
 */
function downloadFile({
  content,
  filename,
  mimeType = 'application/octet-stream',
}: DownloadOptions): void {
  let blob: Blob;

  if (typeof content === 'string') {
    blob = new Blob([content], { type: mimeType });
  } else {
    blob = content;
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Builds a filename like `vase-with-twist-a1b2c3d4.stl` from the artifact title
 * (sluggified) plus a short message-id suffix so repeated exports of the same
 * day don't collide.
 */
function generateDownloadFilename({
  currentMessage,
  fallback = 'parametric-model',
  extension,
}: GenerateDownloadFilenameOptions): string {
  const rawTitle = currentMessage?.content.artifact?.title ?? '';
  const slug = rawTitle
    .replace(/[^a-zA-Z0-9-_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
  const base = slug || fallback;
  const idSuffix = currentMessage?.id ? `-${currentMessage.id.slice(0, 8)}` : '';
  return `${base}${idSuffix}.${extension}`;
}

/**
 * Downloads STL file from blob
 */
export function downloadSTLFile(
  output: Blob,
  currentMessage?: Message | null,
): void {
  const filename = generateDownloadFilename({
    currentMessage,
    extension: 'stl',
  });

  downloadFile({
    content: output,
    filename,
    mimeType: 'application/octet-stream',
  });
}

/**
 * Downloads OpenSCAD code as .scad file
 */
export function downloadOpenSCADFile(
  code: string,
  currentMessage?: Message | null,
): void {
  const filename = generateDownloadFilename({
    currentMessage,
    extension: 'scad',
  });

  downloadFile({
    content: code,
    filename,
    mimeType: 'text/plain',
  });
}

/**
 * Download a specific artifact file (preview STL / manufacturing 3MF / source
 * SCAD) by resolving the role + format and fetching the bytes via the
 * conversation asset proxy.
 *
 * Note: 3MF downloads only succeed on builds where the worker actually
 * produced manufacturing-role bytes. On the current build the WASM does not
 * emit 3MF (B5 evidence outcome B), so the call rejects with "no file" until
 * lib3mf is linked.
 */
export async function downloadArtifactFile(
  conversationId: string,
  artifact: ParametricArtifact,
  role: ArtifactFile['role'],
  format?: ArtifactFile['format'],
  filenameOverride?: string,
): Promise<void> {
  const file = findArtifactFile(artifact.files, role, format);
  if (!file) {
    const ext = `${role} ${format ?? ''}`.trim();
    const message = i18n.t('services.noFileInArtifact', { ns: 'common', ext });
    throw new Error(`${message} "${artifact.title}"`);
  }
  const blob = await downloadConversationAssetBlob(
    conversationId,
    file.storagePath,
  );
  const safeTitle =
    artifact.title.replace(/[^a-zA-Z0-9-_]+/g, '_') || 'artifact';
  downloadFile({
    content: blob,
    filename: filenameOverride ?? `${safeTitle}.${file.format}`,
    mimeType: file.contentType,
  });
}

export const downloadStlArtifact = (
  conversationId: string,
  artifact: ParametricArtifact,
) => downloadArtifactFile(conversationId, artifact, 'preview', 'stl');

export const download3mfArtifact = (
  conversationId: string,
  artifact: ParametricArtifact,
) => downloadArtifactFile(conversationId, artifact, 'manufacturing', '3mf');

export const downloadScadArtifact = (
  conversationId: string,
  artifact: ParametricArtifact,
) => downloadArtifactFile(conversationId, artifact, 'source', 'scad');
